const dictionary = [
  {
    title: 'Hoisting',
    tags: ['variable', 'define'],
    description: `In JavaScript, a variable can be declared after it has been used.\nIn other words; a variable can be used before it has been declared.\nUsing a let or const variable before it is declared will result in a ReferenceError.`,
    example: `
    /** Valid Hoisting **/
    x = 5;                                  // Assign 5 to x
    elem = document.getElementById("demo"); // Find an element
    elem.innerHTML = x;                     // Display x in the element
    var x;                                  // Declare x

    /** Invalid Hoisting **/
    carName = "Volvo"; //will result in a ReferenceError
    alert(carname);
    let carName;`
  },
  {
    title: 'Pure Function',
    tags: ['effect', 'side', 'modify'],
    description: `A pure function will have the following properties;\n\nIt depends only on its own arguments.\nIt wont try to change variables out of its scope.\nIt doesn't produce any side effects.`,
    example: `
    /** Pure Function **/
    function priceAfterTax(productPrice) {
      return (productPrice * 0.20) + productPrice;
    }
      
    /** Impure Function **/
    var tax = 20;
    
    function calculateTax(productPrice) {
      return (productPrice * (tax/100)) + productPrice;
    }`
  },
  {
    title: 'Higher Order Function',
    tags: ['hof', 'parameter', 'return'],
    description: 'A function that accepts and/or returns another function is called a higher-order function.\n\nArray.map, Array.filter, Array.reduce are most common HOFs.',
    example: `
    function utilizePrefixer(prefix) {
      return function(word) {
        return \`\${prefix}\${word}\`
      }
    }
    
    const withMoneySign = utilizePrefixer('$')
    const withCompanyName = utilizePrefixer('Fandango')
    const tenDollars = withMoneySign('9.99')
    const formHeader = withCompanyName(
      ' is the company you will be applying for today',
    )
    
    console.log(tenDollars)
    console.log(formHeader)`
  },
  {
    title: 'Closure',
    tags: ['scope'],
    description: 'A closure is an inner function that has access to the variables in the outer (enclosing) function’s scope chain.\nThe closure has access to variables in three scopes:\n\nVariable in its own scope.\nVariables in the enclosing function’s scope.\nGlobal variables.',
    example: `
    function init() {
      var text = 'Hello World'; // name is a local variable created by init

      // displayText() is the inner function, a closure
      function displayText() { 
        alert(text); // use variable declared in the parent function
      }
      
      displayText();
    }

    init();`
  },
  {
    title: 'Currying',
    tags: ['higher', 'order', 'function'],
    description: `Currying is a technique of evaluating function with multiple arguments, into sequence of functions with single argument. Uses of currying function:\n\nIt helps to avoid passing same variable again and again.\nIt is extremely useful in event handling.`,
    example: `
    /** No Currying **/
    function volume(length, width, height) {
      return length * width * height;
    }
    var result = volume(11,2,3);
    
    /** Currying **/
    function volume(length) {
      return function(width) {
         return function(height) {
            return height * width * length;
         }
      }
    }
    var result = volume(11)(2)(3);`
  },
  {
    title: 'Array.reduce',
    tags: [],
    description: 'The reduce() method reduces the array to a single value.',
    example: `
    const array1 = [1, 2, 3, 4];
    const reducer = (accumulator, item) => accumulator + item;

    // 1 + 2 + 3 + 4
    console.log(array1.reduce(reducer));
    // expected output: 10

    // 5 + 1 + 2 + 3 + 4
    console.log(array1.reduce(reducer, 5));
    // expected output: 15`
  },
  {
    title: 'DOM',
    tags: ['document', 'object', 'model'],
    description: 'DOM stands for Document Object Model and is responsible for how various objects in a document interact with each other. DOM is required for developing web pages, which includes objects like paragraph, links, etc. These objects can be operated to include actions like add or delete. DOM is also required to add extra capabilities to a web page.',
    lang: 'html',
    example: `
    <html>
      <body>
        <p id="demo"></p>
        <script>
          document.getElementById("demo").innerHTML = "Hello World!";
        </script>
      </body>
    </html>`
  },
  {
    title: 'Feature Detection',
    tags: ['browser', 'exists'],
    description: 'Feature detection is a way of determining if a feature exists in certain browsers. A good example is a modern HTML5 feature ‘Location’.',
    example: `
    if (navigator.geolocation) {
      // Detect users location here and do something with that
    }`
  },
  {
    title: 'Feature Inference',
    tags: ['browser', 'available'],
    description: 'Feature Inference is when you have determined a feature exists and assumed the next web technology feature you are implementing unto your app exists as well. Its usually bad practice to assume, so its better to explicitly specify features you want to detect and plan a fallback action.'
  },
  {
    title: 'Use Strict',
    tags: [],
    description: 'Defines that JavaScript code should be executed in "strict mode".\n\nStrict mode makes it easier to write "secure" JavaScript.\nAs an example, in normal JavaScript, mistyping a variable name creates a new global variable.\nIn strict mode, this will throw an error, making it impossible to accidentally create a global variable.',
    example: `
    "use strict";

    /** Using a variable or an object, without declaring it, is not allowed **/
    x = 3.14;
    
    /** Deleting a variable (or object), a function or an undeletable property is not allowed **/
    var x = 3.14;
    delete x; 

    /** Duplicating a parameter name is not allowed **/
    function x(p1, p1) {};

    /** Octal numeric literals are not allowed **/
    var x = 010;

    /** Octal escape characters are not allowed **/
    var x = "\\010";

    /** Writing to a read-only property is not allowed **/
    var obj = {};
    Object.defineProperty(obj, "x", {value:0, writable:false});

    obj.x = 3.14;

    /** The word eval, arguments cannot be used as a variable **/
    var eval = 3.14;`
  },
  {
    title: 'NaN',
    tags: ['not', 'number'],
    description: 'The NaN property represents "Not-a-Number" value. This property indicates that a value is not a legal number.\n\nNaN compares unequal (via ==, !=, ===, and !==) to any other value -- including to another NaN value.\ntypeof NaN is Number.',
    example: `
    NaN === NaN;        // false
    Number.NaN === NaN; // false
    isNaN(NaN);         // true
    isNaN(Number.NaN);  // true
    Number.isNaN(NaN);  // true`
  },
  {
    title: 'isNaN',
    tags: ['not', 'number'],
    description: 'The isNaN() function determines whether a value is an illegal number (Not-a-Number).\n\nThis function returns true if the value equates to NaN. Otherwise it returns false.',
    example: `
    isNaN(123) //false
    isNaN(-1.23) //false
    isNaN(5-2) //false
    isNaN(0) //false
    isNaN('123') //false
    isNaN('Hello') //true
    isNaN('2005/12/12') //true
    isNaN('') //false
    isNaN(true) //false
    isNaN(undefined) //true
    isNaN('NaN') //true
    isNaN(NaN) //true
    isNaN(0 / 0) //true
    isNaN(null) //false`
  },
  {
    title: 'Named Function',
    tags: ['name', 'function', 'define'],
    description: 'Is not equal to 0.3. Numbers are all treated with floating point precision, and as such, may not always yield the expected results.',
    example: `
    function named() {
      // write code here
    }`
  },
  {
    title: 'Scope',
    description: 'Scope determines the accessibility (visibility) of variables. In JavaScript there are three types of scope:\n\nGlobal Scope - Variables and functions having global scope can be accessed from anywhere inside the code.\nLocal Scope - Variables and functions declared inside a function, can be accessed from within the function and not outside of it.\nBlock Scope - Variables can be accessed only inside that block and cannot be accessed outside of it.',
    example: `
    /** Local Variable **/

    // code here can NOT use carName
    function myFunction() {
      var carName = "Volvo";
      // code here CAN use carName
    }
    
    /** Global Variable **/

    var carName = "Volvo";
    // code here can use carName
    function myFunction() {
      // code here can also use carName
    }
    
    /** Block Variable **/
    {
      let x = 45;
    }
    
    console.log(x); // Gives reference error since x cannot be accessed outside of the block`
  },
  {
    title: 'This',
    description: 'The JavaScript this keyword refers to the object it belongs to. It has different values depending on where it is used:\n\nIn a method, this refers to the owner object.\nAlone, this refers to the global object.\nIn a function, this refers to the global object.\nIn a function, in strict mode, this is undefined.\nIn an event, this refers to the element that received the event.\nMethods like call(), and apply() can refer this to any object.',
    example: `
    // refers to the person object
    var person = {
      firstName  : "John",
      lastName   : "Doe",
      fullName   : function() {
        return this.firstName + " " + this.lastName;
      }
    };

    // refers to the Global object
    var x = this;`
  },
  {
    title: 'Attributes - Properties',
    description: 'Attributes are defined by HTML. Properties are accessed from DOM (Document Object Model) nodes.',
    lang: 'html',
    example: `
    // 'id', 'type', 'value' are attibutes in HTML, properties in JavaScript
    <input id="inputId" type="text" value="Name" />`
  },
  {
    title: 'Browser Storage',
    tags: ['local', 'session', 'cookie'],
    description: 'Local Storage – The data is not sent back to the server for every HTTP request (HTML, images, JavaScript, CSS, etc) – reducing the amount of traffic between client and server. It will stay until it is manually cleared through settings or program.\n\nSession Storage – It is similar to local storage; the only difference is while data stored in local storage has no expiration time, data stored in session storage gets cleared when the page session ends. Session Storage will leave when the browser is closed.\n\nCookie - Stores data that has to be sent back to the server with subsequent XHR requests. Its expiration varies based on the type and the expiration duration can be set from either server-side or client-side. Cookies can be made secure by setting the httpOnly flag as true for that cookie. This prevents client-side access to that cookie.',
    example: `
    // getting & setting localStorage
    localStorage.setItem('name', 'Name');
    let name = localStorage.getItem('name');

    // getting & setting sessionStorage
    sessionStorage.setItem('name', 'Name');
    let name = sessionStorage.getItem('name');

    // accessing & setting cookie
    document.cookie = "name=hello world";
    console.log(document.cookie);`
  },
  {
    title: 'Window',
    description: 'The window object is supported by all browsers. It represents the browser\'s window.\n\nAll global JavaScript objects, functions, and variables automatically become members of the window object.\n\nGlobal variables are properties of the window object.\n\nGlobal functions are methods of the window object.',
    example: `
    function windowSize() {
      var w = window.innerWidth;
      var h = window.innerHeight;
      alert("Width: " + w + ", " + "Height: " + h);
    }`
  },
  {
    title: 'Document',
    description: 'The document object represents your web page.\n\nThe document also comes under the window and can be considered as the property of the window.\n\nIf you want to access any element in an HTML page, you always start with accessing the document object.\n\nBelow are some examples of how you can use the document object to access and manipulate HTML.',
    example: `
    document.getElementById('elementId');
    document.getElementsByTagName('tagName');
    document.getElementsByClassName('className');`
  },
  {
    title: 'Emptying Array',
    description: 'There are a number of methods you can use to empty an array.',
    example: `
    arrayList = [];

    arrayList.length = 0;

    arrayList.splice(0, arrayList.length);

    while(arrayList.length) {
      arrayList.pop();
    }`
  },
  {
    title: 'Wrapping entire content of JavaScript',
    description: 'This is an increasingly common practice, employed by many popular JavaScript libraries. This technique creates a closure around the entire contents of the file which, perhaps most importantly, creates a private namespace and thereby helps avoid potential name clashes between different JavaScript modules and libraries.\n\nAnother feature of this technique is to allow for an easy alias for a global variable. This is often used in jQuery plugins.'
  },
  {
    title: 'Escape Character',
    description: 'JavaScript escape characters enable you to write special characters without breaking your application. Escape characters (Backslash) is used when working with special characters like single quotes, double quotes, apostrophes and ampersands. Place backslash before the characters to make it display.',
    example: `
    var exampleString = "This is an example for \\"escape\\" characters.";`
  },
  {
    title: 'Call - Bind - Apply',
    description: 'Use .bind() when you want that function to later be called with a certain context, useful in events.\n\nUse .call() or .apply() when you want to invoke the function immediately, with modification of the context. (does not modify the scope \'this\'.)',
    example: `
    var cylinder = {
      pi: 3.14,
      volume: function(r, h) {
        return this.pi * r * r * h;
      }
    };
    
    /** Call changes pi value with the given one **/
    cylinder.volume.call({pi: 3.14159}, 2, 3);
    
    /** Apply also changes value with the given one like Call, only difference is arguments are passed as array. **/
    cylinder.volume.apply({pi: 3.14159}, [2, 3]);

    /** Bind attaches a new this to given function, the function is not executed instantly like Call or Apply. **/
    var newVolume = cylinder.volume.bind({pi: 3.14159});
    newVolume(2, 3);`
  },
  {
    title: 'Object.freeze - Object.seal',
    description: 'Object.freeze allows us to freeze an object so that existing properties cannot be modified.\n\nObject.seal is slightly different from the freeze. It allows configurable properties but won’t allow new property addition or deletion or properties.',
    example: `
    var marks = {physics: 98, maths:95, chemistry: 91};
    var = finalizedMarks = Object.freeze(marks);
    finalizedMarks.physics = 86; // throws error in strict mode
    console.log(marks); // {physics: 98, maths: 95, chemistry: 91}
    
    Object.isFrozen(finalizedMarks); // returns true
    
    var marks = {physics: 98, maths:95, chemistry: 91};
    Object.seal(marks);
    delete marks.chemistry; // returns false as operation failed
    marks.physics = 95; // Works!
    marks.greek = 86; // Will not add a new property
    
    Object.isSealed(marks); // returns true`
  },
  {
    title: 'Self Invoking Functions',
    description: 'Self Invoking Functions are a little different from normal functions because they are executed immediately where they were declared.',
    example: `
    (function (){
      var a = 12;
      var b = 3;
      console.log(a * b);
    }());`
  },
  {
    title: 'Variables',
    tags: ['var', 'let', 'const'],
    description: 'var - It has a global/function scope. Can be updated or re-declared in its scope.\n\nlet - It has block scope. We can’t re-declare them.\n\nconst - It also has block scope. const represents a constant value, so it can’t be updated or re-declared.',
  },
  {
    title: '== and ===',
    tags: ['operator', 'equal'],
    description: '== - It is a Type Converting Operator. It is only used to compare two values.\n\n=== - It is a Strict Equality Operator. It compares two values as well as their types.',
    example: `
    var x = 5;

    x == 8;	//false	
    x == 5;	//true	
    x == "5";	//true

    x === 5;	//true	
    x === "5";	//false`
  },
  {
    title: 'null and undefined',
    description: 'null - We declare a variable and assign it a value of null. type of (null) “object”\n\nundefined - We declare a variable without assigning any value. type of (undefined) “undefined”',
    example: `
    let a = null;
    let b;

    console.log(a);
    // null
    
    console.log(b);
    //undefined`
  },
  {
    title: 'Function Declaration & Expression',
    description: 'Function Declaration is the normal process of defining a function using the keyword function, its unique name, parameters, and the function body. When we assign a function declaration to a variable then it become Function Expression.\n\nFunction expressions are not hoisted.',
    example: `
    /** Function Declaration **/
    function full_name(first_name, last_name) {
      return first_name + " " + last_name;
    }
    
    /** Function Expression **/
    var addition = function add(value_1, value_2) {
      return value_1 + value_2;
    }`
  },
  {
    title: 'ECMA Script',
    description: 'JavaScript is a scripting language whereas ECMA Script is a collection of guidelines and rules to standardize JavaScript across different web browsers.'
  },
  {
    title: 'Implicit Type Coercion',
    description: 'Implicit type coercion in javascript is automatic conversion of value from one data type to another. It takes place when the operands of an expression are of different data types.',
    example: `
    /** String Coercion **/
    var x = 3;
    var y = "3";
    x + y // Returns "33"

    /** Boolean Coercion **/
    var x = 0;
    var y = 23;
            
    if(x) {
      console.log(x); // The code inside this block will not run since the value of x is 0(Falsy)  
    } 
            
    if(y) {
      console.log(y); // The code inside this block will run since the value of y is 23 (Truthy)
    }`
  },
  {
    title: 'Constructor',
    description: 'Constructor functions are used to create objects in javascript.',
    example: `
    function Person(name,age,gender){
      this.name = name;
      this.age = age;
      this.gender = gender;
    }
    
    
    var person1 = new Person("Vivek", 76, "male");
    console.log(person1);
    
    var person2 = new Person("Courtney", 34, "female");
    console.log(person2);`
  },
  {
    title: 'Recursion',
    description: 'Recursion is a technique to iterate over an operation by having a function call itself repeatedly until it arrives at a result.',
    example: `
    function computeSum(arr){
      if(arr.length === 1){
        return arr[0];
      }
      else{
        return arr.pop() + computeSum(arr);
      }
    }
    
    computeSum([7, 8, 9, 99]); // Returns 123`
  },
  {
    title: 'Memoization',
    description: 'Memoization is a form of caching where the return value of a function is cached based on its parameters. If the parameter of that function is not changed, the cached version of the function is returned.',
    example: `
    function memoizedAddTo256(){
      var cache = {};
    
      return function(num){
        if(num in cache){
          console.log("cached value");
          return cache[num]
    
        }
        else{
          cache[num] = num + 256;
          return cache[num];
        }
      }
    }
    
    var memoizedFunc = memoizedAddTo256();
    
    memoizedFunc(20); // Normal return
    memoizedFunc(20); // Cached return`
  },
  {
    title: 'Temporal Dead Zone',
    description: 'Temporal Dead Zone is a behaviour that occurs with variables declared using let and const keywords. It is a behaviour where we try to access a variable before it is initialized.',
    example: `
    x = 23; // Gives reference error

    let x;
    
    
    function anotherRandomFunc(){
      message = "Hello"; // Throws a reference error
    
      let message;
    }
    anotherRandomFunc();`
  },
  {
    title: 'Rest Parameter',
    tags: ['spread'],
    description: 'Using the rest parameter syntax, we can create functions that can take a variable number of arguments. Any number of arguments will be converted into an array using the rest parameter.',
    example: `
    function extractingArgs(...args){
      return args[1];
    }
    
    // extractingArgs(8,9,1); // Returns 9`
  },
  {
    title: 'Spread Operator',
    description: 'Although the syntax of spread operator is exactly the same as the rest parameter, spread operator is used to spread an array, and object literals. Spread operators are also used where one or more arguments are expected in a function call.',
    example: `
    function addFourNumbers(num1,num2,num3,num4){
      return num1 + num2 + num3 + num4;
    }

    let fourNumbers = [5, 6, 7, 8];

    addFourNumbers(...fourNumbers);
    // Spreads [5,6,7,8] as 5,6,7,8
    
    let array1 = [3, 4, 5, 6];
    let clonedArray1 = [...array1];
    console.log(clonedArray1); // Outputs [3,4,5,6]
    
    let obj1 = {x:'Hello', y:'Bye'};
    let clonedObj1 = {...obj1}; // Spreads and clones obj1
    console.log(obj1);
    
    let obj2 = {z:'Yes', a:'No'};
    let mergedObj = {...obj1, ...obj2}; // Spreads both the objects and merges it
    console.log(mergedObj);
    // Outputs {x:'Hello', y:'Bye',z:'Yes',a:'No'};`
  },
  {
    title: 'Class',
    description: 'Classes provide a new way of declaring constructor functions in javascript.',
    example: `
    class Student{
      constructor(name,rollNumber,grade,section){
        this.name = name;
        this.rollNumber = rollNumber;
        this.grade = grade;
        this.section = section;
      }
    
      // Methods can be directly added inside the class
      getDetails(){
        return 'Name: \${this.name}, Roll no: \${this.rollNumber}, Grade:\${this.grade}, Section:\${this.section}';
      }
    }`
  },
  {
    title: 'Generator Function',
    description: 'Generator functions are declared with the function* keyword. They can be stopped midway and then continue from where it had stopped.\n\nThe generator object consists of a method called next() , this method when called, executes the code until the nearest yield statement, and returns the yield value.',
    example: `
    function* genFunc(){
      yield 3;
      yield 4;
      return 5;
    }

    var generate = genFunc();

    generate.next(); // Returns {value: 3, done:false}
    generate.next(); // Returns {value: 4, done:false}
    generate.next(); // Returns {value: 5, done:true}`
  },
  {
    title: 'WeakMap',
    description: 'In javascript, Map is used to store key-value pairs. The key-value pairs can be of both primitive and non-primitive types. WeakMap is similar to Map with key differences:\n\nThe keys and values in weakmap should always be an object.\nIf there are no references to the object, the object will be garbage collected.',
    example: `
    const map1 = new Map();
    map1.set('Value', 1);
    
    const map2 = new WeakMap();
    map2.set('Value', 2.3); // Throws an error
    
    let obj = {name:"Vivek"};
    const map3 = new WeakMap();
    map3.set(obj, {age:23});`
  },
  {
    title: 'WeakSet',
    description: 'In javascript, Set is a collection of unique and ordered elements. Just like Set, WeakSet is also a collection of unique and ordered elements with some key differences:\n\nWeakset contains only objects and no other type.\nAn object inside the weakset is referenced weakly. This means, if the object inside the weakset does not have a reference, it will be garbage collected.\nUnlike Set, WeakSet only has three methods, add() , delete() and has().',
    example: `
    const newSet = new Set([4, 5, 6, 7]);
    console.log(newSet);// Outputs Set {4,5,6,7}
    
    const newSet2 = new WeakSet([3, 4, 5]); //Throws an error
    
    
    let obj1 = {message:"Hello world"};
    const newSet3 = new WeakSet([obj1]);
    console.log(newSet3.has(obj1)); // true`
  },
  {
    title: 'Destructuring',
    description: 'Object destructuring is a new way to extract elements from an object or an array.',
    example: `
    /** Object Destructuring **/
    const classDetails = {
      strength: 78,
      benches: 39
    }
    
    const {strength, benches} = classDetails;
    
    console.log(strength); // Outputs 78
    console.log(benches); // Outputs 39
    

    /** Array Destructuring **/
    const arr = [1, 2];
    const [first, second] = arr;
    
    console.log(first); // Outputs 1
    console.log(second); // Outputs 2`
  },
  {
    title: 'Errors',
    description: 'Load Time Errors - Errors which come up when loading a web page like improper syntax errors are known as Load time errors and it generates the errors dynamically.\n\nRun Time Errors - Errors that come due to misuse of the command inside the HTML language.\n\nLogical Errors - These are the errors that occur due to the bad logic performed on a function which is having different operation.'
  },
  {
    title: 'onLoad - onDocumentReady',
    description: 'The onload function is not run until all the information on the page is loaded. This leads to a substantial delay before any code is executed.\n\nonDocumentReady loads the code just after the DOM is loaded. This allows early manipulation of the code.'
  },
  {
    title: 'Deferred Script',
    description: 'DOM stands for Document Object Model and is responsible for how various objects in a document interact with each other. DOM is required for developing web pages, which includes objects like paragraph, links, etc. These objects can be operated to include actions like add or delete. DOM is also required to add extra capabilities to a web page.'
  },
  {
    title: 'Paradigm',
    description: 'A programming paradigm is a style, or “way,” of programming, they are not meant to be mutually exclusive. A single program can feature multiple paradigms.\n\nFunctional Programming - Functional programming is a programming pattern centered around functions is involves closures and lambdas to execute some certain task. Avoids shared state & mutable data and side-effects.\nObject Oriented Programming - Object-oriented programming is a pattern of programming using objects, it consists of classes objects and prototypes. ',
    example: `
    /** Functional Programming **/
    const developers = [
      {
        name: "James",
        yearsOfExperience: 2,
        language: "javascript",
      },
      {
        name: "Adam",
        yearsOfExperience: 5,
        language: "javascript",
      }
    ];
    
    function listDevelopers() {
      developers.forEach((developer) => {
        const { name, experience_year, language } = developer;
        console.log(
          \`\${name} has \${yearsOfExperience} years of experience in \${language}\`
        );
      });
    }
    
    /** Object Oriented Programming **/
    const John = {
      skills: ["Python", "Javascript", "SQL"],
      age: 22,
      language: "English",
      skilled: function skilled() {
        if (this.skills.length > 2) {
          return "He's got some skills";
        } else {
          return "Fair Enough";
        }
      },
    };`
  },
  {
    title: 'Inheritance',
    description: 'Class Inheritance - The extends keyword is used to create a class inheritance. Classes are immutable and cannot be modified at runtime. Classes may not support multiple inheritance. Also it\'s more complicated than prototypal inheritance.\n\nPrototypal Inheritance - instances inherit directly from other objects. Instances are typically instantiated via factory functions or `Object.create()`. Prototypes are flexible, they maybe mutable or immutable. They can inherit from multiple prototypes. It\'s fairly simple compared to class inheritance.',
    example: `
    /** Class Inheritance **/
    class Car {
      constructor(brand) {
        this.carname = brand;
      }
      present() {
        return 'I have a ' + this.carname;
      }
    }
    
    class Model extends Car {
      constructor(brand, mod) {
        super(brand);
        this.model = mod;
      }
      show() {
        return this.present() + ', it is a ' + this.model;
      }
    }
    
    /** Prototypal Inheritance **/
    function Dog (name, energy, breed) {
      this.name = name
      this.energy = energy
      this.breed = breed
    }
    
    Dog.prototype.eat = function (amount) {
      console.log(\`\${this.name} is eating.\`)
      this.energy += amount
    }
    
    const charlie = new Dog('Charlie', 10, 'Goldendoodle')`
  },
  {
    title: 'Composition',
    description: 'Composition is about creating small functions and creating bigger and more complete functions with them. Think of a function as a brick, composition is how you would make those bricks work together to build a wall or a house.',
    example: `
    // We have some behaviors
    const canSayHi = self => ({
      sayHi: () => console.log(\`Hi! I'm \${self.name}\`)
    });
    const canEat = () => ({
      eat: food => console.log(\`Eating \${food}...\`)
    });

    // Combined previous behaviours
    const socialBehaviors = self => Object.assign({}, canSayHi(self), canEat());

    const alligator = name => {
      const self = {
        name
      };

      const alligatorBehaviors = self => ({
        bite: () => console.log("Yum yum!")
      });

      return Object.assign(self, socialBehaviors(self), alligatorBehaviors(self));
    };


    const jack = alligator("jack");
    jack.sayHi(); // Hi! I'm jack
    jack.eat("Banana"); // Eating Banana...
    jack.bite(); // Yum yum!
    
    /** Class Composition **/
    const MixinA = superclass => class extends superclass {};
    const MixinB = superclass => class extends superclass {};
    const MixinC = superclass => class extends superclass {};
    const MixinD = superclass => class extends superclass {};

    class Base {}
    class Child extends MixinD(MixinC(MixinB(MixinA(Base)))) {}`
  },
  {
    title: 'Virtual DOM',
    description: 'Virtual DOM is a concept where a virtual representation of the real DOM is kept inside the memory and is synced with the real DOM by a library such as ReactDOM.'
  },
  {
    title: 'Prop Drilling',
    description: 'Sometimes while developing React applications, there is a need to pass data from a component that is higher in the hierarchy to a component that is deeply nested.\n\nTo pass data between such components, we pass props from a source component, and keep passing the prop to the next component in the hierarchy till we reach the deeply nested component.\n\nThe disadvantage of using prop drilling is that the components that should otherwise be not aware of the data have access to the data.'
  },
  {
    title: '* Partial Function',
    tags: ['higher', 'order', 'function'],
    description: 'Higher Order Function that doesnt take all arguments.'
  },
];

export default dictionary;